/* The /ai-open page.
 *
 * The thing this tool must never do is show Adobe's apology and call it a
 * result. Every one of the four real fixtures paints "This file was saved
 * without PDF Content" on its PDF page, which is what other viewers display —
 * so the interface leads with the artwork parsed out of the private stream, and
 * says plainly why the file looked broken elsewhere.
 *
 * No worker: parsing 873 KB of Illustrator PostScript takes 4 ms, and the
 * container work is dominated by DecompressionStream, which is off-thread
 * already. A worker here would only add a copy of the file.
 */

import { sniff, readPdfContainer, inflateAiArt, KIND } from './ai-container.js'
import { parseArt, artBounds, toSvg } from './ai-art.js'
import { reportShape } from '../file-shape.js'
import { suggestOtherTool } from '../file-signature.js'

const T = window.__AI_I18N || {}
const t = (k, fallback, vars) => {
  let s = T[k] || fallback || k
  /* Longest placeholder first — ':n' is a prefix of ':name'. */
  if (vars) {
    for (const [n, v] of Object.entries(vars).sort((a, b) => b[0].length - a[0].length)) {
      s = s.split(':' + n).join(String(v))
    }
  }
  return s
}
const $ = s => document.querySelector(s)
const el = (tag, cls, text) => {
  const n = document.createElement(tag)
  if (cls) n.className = cls
  if (text != null) n.textContent = text
  return n
}

const state = { name: '', doc: null, svg: null, info: null, limit: null }

function setStatus(msg, kind = 'info') {
  const box = $('#ai-status')
  if (!box) return
  box.hidden = !msg
  box.className = 'ai-status lg-card ai-status--' + kind
  box.textContent = msg || ''
}

const fmtBytes = n => {
  const u = [t('unit_b', 'B'), t('unit_kb', 'KB'), t('unit_mb', 'MB')]
  let i = 0
  let v = n
  while (v >= 1024 && i < u.length - 1) { v /= 1024; i++ }
  return `${v < 10 && i ? v.toFixed(1) : Math.round(v)} ${u[i]}`
}

async function open(file) {
  state.name = file.name
  setStatus(t('opening', 'Reading the file…'))
  $('#ai-workspace').hidden = true
  $('#ai-summary').hidden = true

  const bytes = new Uint8Array(await file.arrayBuffer())
  const kind = sniff(bytes)
  if (kind === KIND.UNKNOWN) {
    setStatus(t('err_not_ai', 'This is not an Illustrator file.'), 'error')
    try { window.__stage?.('error') } catch (_) {}
    suggestOtherTool(file, 'ai-open', document.querySelector('#ai-status'), { text: t('suggest_text'), link: t('suggest_link') })
    state.limit?.abort()
    return
  }

  let art = null
  let info = { kind, size: bytes.length, pdfNotice: false, creatorVersion: null, privateBytes: 0 }

  if (kind === KIND.PDF) {
    const c = await readPdfContainer(bytes)
    info = { ...info, ...c }
    art = c.art
    /* The notice is the whole reason this tool exists, so it is detected rather
       than inferred: a page that paints, plus artwork living only in the private
       stream, is exactly "saved without PDF compatibility". */
    info.pdfNotice = !!(c.art && c.art.length)
  } else {
    art = await inflateAiArt(bytes)
  }

  if (!art || !art.length) {

    /* Nothing could be read out. This is the case worth hearing about most:
       the file may be one saved without "Create PDF Compatible File", where the
       PDF half paints Adobe's notice rather than the artwork. */
    try {
      reportShape({
        format: 'ai', version: info.kind, size: info.size, ok: false,
        reason: 'no-art', flags: info.pdfNotice ? ['pdf-notice'] : [],
      })
    } catch (_) {}
    setStatus(t('err_no_art', 'No artwork could be read out of this file.'), 'error')
    try { window.__stage?.('error') } catch (_) {}
    suggestOtherTool(file, 'ai-open', document.querySelector('#ai-status'), { text: t('suggest_text'), link: t('suggest_link') })
    state.limit?.abort()
    return
  }

  const doc = parseArt(art)
  state.doc = doc
  state.info = info
  state.svg = toSvg(doc, { width: 1200 })

  if (!doc.items.length) {
    setStatus(t('warn_no_paths', 'The file opened, but it holds no vector shapes — its content is text or placed images.'), 'warn')
  } else {
    setStatus('')
  }

  renderSummary()
  renderArt()

  /* Which flavours of .ai actually arrive, and which PostScript operators we
     step over. `lost` keys are operator names — `sh`, `Do` — never content;
     font and layer NAMES are counted, never sent. */
  try {
    reportShape({
      format: 'ai',
      version: info.kind,
      size: info.size,
      ok: true,
      flags: [].concat(
        info.pdfNotice ? ['pdf-notice'] : [],
        doc.items.length ? [] : ['no-shapes'],
        [...doc.lost.keys()].slice(0, 8).map(op => 'lost-' + op),
      ),
      counts: {
        paths: doc.stats.paths,
        points: doc.stats.points,
        layers: doc.layers.length,
        fonts: doc.fonts.length,
        texts: doc.texts.length,
        skipped: doc.skipped.size,
      },
    })
  } catch (_) { /* telemetry must never break the tool */ }

  try { window.__stage?.('complete') } catch (_) {}
  $('#ai-workspace').hidden = false
  state.limit?.commit()
  window.__aeToolComplete?.({ paths: doc.stats.paths, kind })
}

function renderSummary() {
  const box = $('#ai-summary')
  const d = state.doc
  const i = state.info
  box.innerHTML = ''
  box.hidden = false

  const chips = el('div', 'ai-chips')
  const chip = (label, value, kind = '') => {
    if (value == null || value === '') return
    const c = el('span', 'ai-chip' + (kind ? ' ai-chip--' + kind : ''))
    c.append(el('b', null, label), document.createTextNode(' '), el('span', null, String(value)))
    chips.append(c)
  }
  chip(t('chip_kind', 'Format'), i.kind === KIND.PDF
    ? t('kind_modern', 'Illustrator 9+ (PDF container)')
    : t('kind_legacy', 'Illustrator 8 or older (EPS)'))
  chip(t('chip_size', 'Size'), fmtBytes(i.size))
  const b = artBounds(d)
  if (b.w) chip(t('chip_artboard', 'Artwork'), `${Math.round(b.w)} × ${Math.round(b.h)} pt`)
  chip(t('chip_paths', 'Shapes'), d.stats.paths)
  if (d.layers.length) chip(t('chip_layers', 'Layers'), d.layers.length)
  if (d.fonts.length) chip(t('chip_fonts', 'Fonts'), d.fonts.length)
  if (d.creator) chip(t('chip_creator', 'Made by'), d.creator.replace(/^Adobe Illustrator\(R\)\s*/, 'Illustrator '))
  box.append(chips)

  const notes = el('ul', 'ai-notes')
  if (i.pdfNotice) {
    notes.append(el('li', 'ai-note--key', t('note_no_pdf_compat',
      'This file was saved without the "Create PDF Compatible File" option. Other programs open it and show an Adobe notice instead of the artwork — what you see here was read out of the Illustrator data itself.')))
  }
  if (d.fonts.length) {
    notes.append(el('li', null, t('note_fonts', 'Fonts the document asks for: :list', { list: d.fonts.join(', ') })))
  }
  if (d.texts.length || (!d.items.length && d.stats.points)) {
    notes.append(el('li', null, t('note_text', 'Text is shown as it is stored, not re-typeset: Illustrator lays type out with the embedded fonts, and guessing at that would move the words.')))
  }
  /* Only report what the eye can miss. Every unhandled operator used to be
     counted here, so a flawless render of the legacy fixture still announced
     "3 kinds of instruction were skipped (j d J)" — line joins, dashes and
     caps. A warning that fires on a perfect result teaches people to ignore
     warnings. */
  const lost = [...d.lost.entries()].sort((a, c) => c[1] - a[1]).slice(0, 6)
  if (lost.length) {
    notes.append(el('li', null, t('note_skipped',
      'Not everything was drawn: :n kinds of instruction were skipped (:list). Gradients, meshes and effects are among them.',
      { n: d.lost.size, list: lost.map(([o]) => o).join(' ') })))
  }
  if (notes.children.length) {
    box.append(el('h4', 'ai-sub', t('notes_title', 'What the file says about itself')), notes)
  }
}

function renderArt() {
  const host = $('#ai-canvas')
  host.innerHTML = ''
  if (!state.svg) {
    host.append(el('p', 'ai-empty', t('no_preview', 'Nothing could be drawn from this file.')))
    return
  }
  const wrap = el('div', 'ai-svg')
  wrap.innerHTML = state.svg
  host.append(wrap)
}

function download(blob, filename) {
  if (window.__downloadFile) window.__downloadFile(blob, filename)
}

const stem = () => state.name.replace(/\.ai$/i, '') || 'artwork'

function exportSvg() {
  if (!state.svg) return
  download(new Blob([state.svg], { type: 'image/svg+xml' }), stem() + '.svg')
}

async function exportPng(scale = 2) {
  if (!state.svg) return
  const b = artBounds(state.doc)
  const w = Math.max(1, Math.round(b.w * scale))
  const h = Math.max(1, Math.round(b.h * scale))
  const img = new Image()
  const url = URL.createObjectURL(new Blob([state.svg], { type: 'image/svg+xml' }))
  try {
    await new Promise((res, rej) => { img.onload = res; img.onerror = rej; img.src = url })
    const c = document.createElement('canvas')
    c.width = w; c.height = h
    const ctx = c.getContext('2d')
    ctx.fillStyle = '#fff'
    ctx.fillRect(0, 0, w, h)
    ctx.drawImage(img, 0, 0, w, h)
    const blob = await new Promise(r => c.toBlob(r, 'image/png'))
    if (blob) download(blob, stem() + '.png')
  } finally {
    URL.revokeObjectURL(url)
  }
}

function wire() {
  const drop = document.getElementById('aiDrop')
  /* The event is `tool-uploader:file`, singular, and the detail carries either
     `files` or a lone `file`. Guessing the plural name costs nothing at load
     time and everything at drop time: the page looks perfect and does nothing. */
  drop?.addEventListener('tool-uploader:file', async ev => {
    const file = ev.detail?.files?.length ? ev.detail.files[0] : ev.detail?.file
    if (!file) return
    /* Funnel stages. Without them a tool can only report that it finished —
       never that someone arrived, picked a file and got nothing, which is the
       case worth knowing about because that person leaves without writing in. */
    try { window.__stage?.('file_chosen', { mb: Math.round((file.size || 0) / 1048576) }) } catch (_) {}
    state.limit = window.__toolLimit?.begin('ai-open', file.name + ':' + file.size) || null
    try {
      await open(file)
    } catch (err) {
      try { window.__stage?.('error') } catch (_) {}
    suggestOtherTool(file, 'ai-open', document.querySelector('#ai-status'), { text: t('suggest_text'), link: t('suggest_link') })
    state.limit?.abort()
      setStatus(t('err_open', 'Could not read this file: ') + (err?.message || err), 'error')
      window.__handleError?.(err, 'ai-open')
    }
  })
  drop?.addEventListener('tool-uploader:clear', () => {
    state.doc = null; state.svg = null
    $('#ai-workspace').hidden = true
    $('#ai-summary').hidden = true
    setStatus('')
  })
  $('#ai-export-svg')?.addEventListener('click', exportSvg)
  $('#ai-export-png')?.addEventListener('click', () => exportPng(2))
}

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', wire)
else wire()
